<div class="min-vh-100 bg-light">
  <app-header [page]="headerPage"></app-header>

  <main class="container-fluid px-3 px-lg-4 py-4">
    <button class="btn btn-link px-0 text-decoration-none mb-3" type="button" [routerLink]="[basePath]">← Back to document requests</button>

    <div *ngIf="loading" class="card border-0 shadow-sm"><div class="card-body text-center py-5"><div class="spinner-border spinner-border-sm me-2"></div>Loading request...</div></div>
    <div *ngIf="error" class="alert alert-danger">{{ error }}</div>

    <ng-container *ngIf="!loading && request">
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <div class="d-flex flex-wrap align-items-center gap-2 mb-1"><h4 class="fw-bold mb-0">{{ request.title }}</h4><span class="badge" [ngClass]="statusClass(request.status)">{{ request.status }}</span><span *ngIf="request.isBlocking" class="badge text-bg-dark">Blocking</span></div>
          <div class="text-muted">{{ request.scopeType }} request · {{ request.requestedAt | date:'medium' }}</div>
        </div>
        <button class="btn btn-outline-secondary btn-sm" type="button" (click)="load()">Refresh</button>
      </div>

      <div class="alert alert-danger shadow-sm" *ngIf="request.status === 'AwaitingReplacement'"><strong>Replacement requested.</strong> {{ request.lastRejectionReason }}</div>
      <div class="alert alert-success shadow-sm" *ngIf="request.status === 'Approved'"><strong>Approved.</strong> Deposit status: {{ request.depositStatus || 'not applicable' }}.</div>

      <div class="row g-4">
        <div class="col-xl-8">
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body">
              <h5 class="fw-bold">Request instructions</h5>
              <p class="preserve-lines">{{ request.instructions }}</p>
              <div *ngIf="request.reason" class="bg-light rounded p-3 small"><span class="text-muted">Reason:</span> {{ request.reason }}</div>
            </div>
          </div>

          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white fw-bold">Document submissions</div>
            <div class="card-body">
              <div *ngIf="!request.submissions.length" class="text-muted">No submission has been uploaded.</div>
              <div class="border rounded p-3 mb-3" *ngFor="let submission of request.submissions">
                <div class="d-flex flex-wrap justify-content-between gap-3">
                  <div><div class="fw-semibold">Attempt {{ submission.attemptNumber }} · {{ submission.fileName }}</div><div class="small text-muted">{{ submission.mimeType }} · {{ fileSize(submission.fileSizeBytes) }} · {{ submission.createdAt | date:'medium' }}</div></div>
                  <div class="d-flex gap-2 align-items-start"><span class="badge" [ngClass]="statusClass(submission.status)">{{ submission.status }}</span><button *ngIf="submission.status !== 'AwaitingUpload'" class="btn btn-outline-primary btn-sm" type="button" (click)="download(submission)" [disabled]="downloadingId === submission.id">{{ downloadingId === submission.id ? 'Opening...' : 'Open document' }}</button></div>
                </div>
                <div *ngIf="submission.rejectionReason" class="alert alert-danger py-2 px-3 mt-3 mb-0 small"><strong>Rejected:</strong> {{ submission.rejectionReason }}</div>
                <div *ngIf="submission.reviewNotes" class="small mt-3"><span class="text-muted">Review notes:</span> {{ submission.reviewNotes }}</div>
              </div>
            </div>
          </div>

          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white fw-bold">Audit history</div>
            <div class="card-body">
              <div class="timeline-item" *ngFor="let action of request.actions">
                <div class="d-flex justify-content-between gap-3"><div class="fw-semibold">{{ action.action }}</div><div class="small text-muted">{{ action.createdAt | date:'medium' }}</div></div>
                <div class="small text-muted" *ngIf="action.previousStatus || action.newStatus">{{ action.previousStatus || '—' }} → {{ action.newStatus || '—' }}</div>
                <div class="small mt-1" *ngIf="action.notes">{{ action.notes }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-4">
          <div class="card border-0 shadow-sm mb-4 sticky-xl-top action-card">
            <div class="card-body">
              <h5 class="fw-bold">Review action</h5>
              <label class="form-label small">Review notes</label>
              <textarea class="form-control mb-3" rows="3" [(ngModel)]="reviewNotes" placeholder="Optional internal review notes"></textarea>

              <button *ngIf="canStartReview()" class="btn btn-warning w-100 mb-2" type="button" (click)="startReview()" [disabled]="actionLoading">Start review</button>
              <button *ngIf="canDecide()" class="btn btn-success w-100 mb-3" type="button" (click)="approve()" [disabled]="actionLoading"><span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-2"></span>Approve document</button>

              <ng-container *ngIf="canDecide()">
                <label class="form-label small text-danger">Rejection reason shown to user</label>
                <textarea class="form-control border-danger mb-2" rows="4" [(ngModel)]="rejectionReason" placeholder="Explain exactly what is wrong and what replacement is required"></textarea>
                <button class="btn btn-outline-danger w-100" type="button" (click)="reject()" [disabled]="actionLoading || !rejectionReason.trim()">Reject and request replacement</button>
              </ng-container>

              <div *ngIf="!canStartReview() && !canDecide()" class="text-muted small">No review action is available while this request is {{ request.status }}.</div>
            </div>
          </div>

          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <h6 class="fw-bold">Request details</h6>
              <dl class="row small mb-0">
                <dt class="col-5 text-muted">User ID</dt><dd class="col-7 text-end font-monospace text-break">{{ request.userId }}</dd>
                <dt class="col-5 text-muted">Deposit ID</dt><dd class="col-7 text-end font-monospace text-break">{{ request.depositId || '—' }}</dd>
                <dt class="col-5 text-muted">Amount</dt><dd class="col-7 text-end">{{ request.depositAmount | number:'1.2-2' }} {{ request.depositCurrency || '' }}</dd>
                <dt class="col-5 text-muted">Deposit status</dt><dd class="col-7 text-end">{{ request.depositStatus || '—' }}</dd>
                <dt class="col-5 text-muted">Document status</dt><dd class="col-7 text-end">{{ request.depositDocumentReviewStatus || '—' }}</dd>
                <dt class="col-5 text-muted">Attempt</dt><dd class="col-7 text-end">{{ request.currentAttemptNumber }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <app-footer></app-footer>
  </main>
</div>
